import { OrgOption, useAllUsersOrgs } from '@weenat/client/dist/hooks/useAllUsersOrgs'
import { useIntl } from '@weenat/wintl'
import FarmsList from 'app/src/dashboard/components/DashboardMap/FarmsList'
import useDisclosure from 'app/src/hooks/useDisclosure'
import { useToken } from 'app/state'
import { useField } from 'formik'
import { isNil } from 'lodash-es'
import { useCallback, useMemo, useState } from 'react'
import Icons from '../Icons'
import Text from '../Text'
import InputFieldContainer from './InputFieldContainer'
import InvisibleTextInput from './InvisibleInput'
import { Dropdown, DropdownContent, DropdownTrigger } from './Select/SelectDropdown'

const rightAdornment = <Icons.Unfold $size='lg' />

interface SelectFarmFieldPrimitiveProps {
  label: string | undefined
  isRequired?: boolean
  isDisabled?: boolean
  isLoading?: boolean
  placeholder?: string
  value: number
  onChange: (value: number) => void
  options?: OrgOption[]
}

export const SelectFarmFieldPrimitive: FC<SelectFarmFieldPrimitiveProps> = ({
  label,
  isRequired,
  placeholder: placeholderFromProps,
  isDisabled,
  isLoading: isLoadingFromProps = false,
  value,
  onChange,
  options
}) => {
  const [token] = useToken()
  const [searchQuery, setSearchQuery] = useState<string>('')
  const { t } = useIntl()

  const { isOpen, close, toggle } = useDisclosure()

  const { farmsOptions, isLoading: isFetchingOptions } = useAllUsersOrgs({
    enabled: !isNil(token) && isNil(options)
  })

  const isLoading = isLoadingFromProps || isFetchingOptions

  const defaultPlaceholder = t('actions.select_farm')

  const placeholder = placeholderFromProps ?? defaultPlaceholder

  const filteredOptions = useMemo(
    () =>
      farmsOptions.filter(
        (option) =>
          isNil(searchQuery) || option.label.toLowerCase().includes(searchQuery.toLowerCase())
      ),
    [farmsOptions, searchQuery]
  )

  const selectedFarm = useMemo(() => {
    return farmsOptions.find((farm) => farm.value === value)
  }, [farmsOptions, value])

  const displayedValue = useMemo(() => {
    return selectedFarm ? selectedFarm.label : undefined
  }, [selectedFarm])

  const onSelection = useCallback(
    (id: number) => {
      onChange(id)
      close()
    },
    [close, onChange]
  )

  return (
    <Dropdown isVisible={isOpen} onVisibleChange={toggle}>
      <DropdownTrigger>
        <InputFieldContainer
          onPress={toggle}
          label={label}
          isRequired={isRequired}
          rightAdornment={rightAdornment}
          $isDisabled={isDisabled || farmsOptions.length <= 1}
          isLoading={isLoading}
          value={displayedValue}
        >
          <Text $color={isNil(displayedValue) ? 'grayscale.700' : undefined}>
            {!isNil(displayedValue) ? displayedValue : placeholder}
          </Text>
        </InputFieldContainer>
      </DropdownTrigger>
      <DropdownContent
        Header={
          <Flex $backgroundColor={'grayscale.100'} $p='md' $alignItems='center'>
            <Icons.Search $size='md' />
            <InvisibleTextInput
              shouldFocusOnMount
              autoComplete='on'
              autoCorrect='off'
              type='text'
              value={searchQuery}
              onChange={(e) => {
                if (!isNil(e.target)) setSearchQuery(e.target.value)
              }}
              placeholder={placeholder}
            />
          </Flex>
        }
      >
        <FarmsList
          isOpen={isOpen}
          close={close}
          farms={filteredOptions}
          onFarmSelection={onSelection}
          selectedFarm={selectedFarm}
        />
      </DropdownContent>
    </Dropdown>
  )
}

interface SelectFarmFieldProps extends Omit<SelectFarmFieldPrimitiveProps, 'value' | 'onChange'> {
  /** default is organizationId */
  fieldName?: string
}

const SelectFarmField: FC<SelectFarmFieldProps> = ({
  fieldName,
  label,
  isRequired,
  placeholder,
  isDisabled,
  isLoading,
  options
}) => {
  const [field, _, helpers] = useField(fieldName ?? 'organizationId')

  const onFarmSelection = useCallback(
    (id: number) => {
      helpers.setValue(id)
    },
    [helpers]
  )

  return (
    <SelectFarmFieldPrimitive
      value={field.value}
      onChange={onFarmSelection}
      label={label}
      isDisabled={isDisabled}
      isRequired={isRequired}
      isLoading={isLoading}
      placeholder={placeholder}
      options={options}
    />
  )
}

export default SelectFarmField
